// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — features/wms/wmsService.ts
//  WMS: Transferência entre depósitos + Cycle Counting (contagem cíclica)
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';

export interface PaginadoResponse<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

/** Filtros comuns das listas (servidor). Período = dia LOCAL, yyyy-MM-dd. */
export interface FiltrosListaWms {
  busca?: string;
  dtInicio?: string;
  dtFim?: string;
  ordenarPor?: string;
  decrescente?: boolean;
}

const paramsLista = (f: FiltrosListaWms = {}) => ({
  busca: f.busca?.trim() || undefined,
  dtInicio: f.dtInicio || undefined,
  dtFim: f.dtFim || undefined,
  ordenarPor: f.ordenarPor || undefined,
  decrescente: f.decrescente,
});

// ── Transferência ─────────────────────────────────────────────────────────────

export interface TransferenciaResumo {
  id: number; numero: number;
  localOrigemId: number; localOrigem: string | null;
  localDestinoId: number; localDestino: string | null;
  statusId: number; status: string;
  dtEmissao: string; valorTotal: number; valorFrete: number; qtdItens: number;
}

export interface TransferenciaItem {
  id: number; item: number; produtoId: number; produto: string | null; subProdutoId: number;
  qtdEmitida: number; qtdRecebida: number; qtdDivergencia: number;
  custoUnitEmissao: number; custoUnitDestino: number; freteRateado: number;
  motivoDivergenciaId: number; motivoDivergencia: string; observacaoDivergencia: string | null;
}

export interface TransferenciaDetalhe {
  id: number; numero: number;
  localOrigemId: number; localOrigem: string | null;
  localDestinoId: number; localDestino: string | null;
  statusId: number; status: string;
  dtEmissao: string; dtAprovacao: string | null; dtEnvio: string | null;
  dtRecebimento: string | null; dtCancelamento: string | null;
  valorTotal: number; valorFrete: number; valorSeguro: number;
  observacao: string | null; motivoCancelamento: string | null;
  itens: TransferenciaItem[];
}

export interface CriarTransferenciaRequest {
  localOrigemId: number; localDestinoId: number;
  valorFrete: number; valorSeguro: number; observacao?: string;
  itens: { produtoId: number; qtdEmitida: number; custoUnitEmissao: number }[];
}

export interface ConferenciaRequest {
  itens: { itemId: number; qtdRecebida: number; motivoDivergencia: number; observacaoDivergencia?: string }[];
}

export interface RegraAprovacao {
  id: number; descricao: string; tipoGatilhoId: number; tipoGatilho: string;
  valorMinimo: number | null; produtoId: number | null;
  localOrigemId: number | null; localDestinoId: number | null; isAtivo: boolean;
  produto?: string | null; localOrigem?: string | null; localDestino?: string | null;
}

export interface SalvarRegraRequest {
  descricao: string; tipoGatilho: number; valorMinimo?: number | null;
  produtoId?: number | null; localOrigemId?: number | null; localDestinoId?: number | null; isAtivo: boolean;
}

const T = '/api/transferencias-estoque';

/** Filtros server-side da lista (FrmCadTransferencia: De/Até, Status, origem/destino). */
export interface TransferenciaFiltros {
  dtInicio?: string; dtFim?: string; localOrigemId?: number; localDestinoId?: number; numero?: number;
}

export const transferenciaService = {
  /** Lista paginada; status, busca (número/observação), período de emissão, origem/destino/número e ordenação no servidor. */
  listar: (pagina = 1, tamanhoPagina = 20, statusId = -1, filtros: FiltrosListaWms & TransferenciaFiltros = {}) =>
    apiClient.get<PaginadoResponse<TransferenciaResumo>>(T, {
      params: {
        pagina, tamanhoPagina, statusId, ...paramsLista(filtros),
        localOrigemId: filtros.localOrigemId || undefined,
        localDestinoId: filtros.localDestinoId || undefined,
        numero: filtros.numero || undefined,
      },
    }).then(r => r.data),
  obter: (id: number) => apiClient.get<TransferenciaDetalhe>(`${T}/${id}`).then(r => r.data),
  criar: (req: CriarTransferenciaRequest) => apiClient.post<{ mensagem: string; id: number }>(T, req).then(r => r.data),
  aprovar: (id: number) => apiClient.post<{ mensagem: string }>(`${T}/${id}/aprovar`).then(r => r.data),
  despachar: (id: number) => apiClient.post<{ mensagem: string }>(`${T}/${id}/despachar`).then(r => r.data),
  conferencia: (id: number, req: ConferenciaRequest) => apiClient.post<{ mensagem: string }>(`${T}/${id}/conferencia`, req).then(r => r.data),
  concluir: (id: number) => apiClient.post<{ mensagem: string }>(`${T}/${id}/concluir`).then(r => r.data),
  cancelar: (id: number, motivo: string) => apiClient.post<{ mensagem: string }>(`${T}/${id}/cancelar`, { motivo }).then(r => r.data),
  // regras
  listarRegras: () => apiClient.get<RegraAprovacao[]>(`${T}/regras-aprovacao`).then(r => r.data),
  criarRegra: (req: SalvarRegraRequest) => apiClient.post(`${T}/regras-aprovacao`, req).then(r => r.data),
  atualizarRegra: (id: number, req: SalvarRegraRequest) => apiClient.put(`${T}/regras-aprovacao/${id}`, req).then(r => r.data),
  excluirRegra: (id: number) => apiClient.delete(`${T}/regras-aprovacao/${id}`).then(r => r.data),
};

// ── Cycle Counting ──────────────────────────────────────────────────────────

export interface CicloContagem {
  id: number; nome: string; classificacaoAbc: string; frequenciaDias: number;
  localEstoqueId: number; localEstoque: string | null;
  dtUltimoCiclo: string | null; dtProximoCiclo: string | null; isAtivo: boolean;
}
export interface SalvarCicloRequest {
  nome: string; classificacaoAbc: string; frequenciaDias: number; localEstoqueId: number; isAtivo: boolean;
}

export interface ContagemResumo {
  id: number; numero: number; cicloId: number | null;
  localEstoqueId: number; localEstoque: string | null; classificacaoAbc: string;
  statusId: number; status: string; dtContagem: string; qtdItens: number; qtdContados: number;
}
export interface ContagemItem {
  id: number; produtoId: number; produto: string | null; subProdutoId: number;
  saldoSistema: number; qtdContada: number; divergencia: number; isContado: boolean;
}
export interface ContagemDetalhe {
  id: number; numero: number; cicloId: number | null;
  localEstoqueId: number; localEstoque: string | null; classificacaoAbc: string;
  statusId: number; status: string; dtContagem: string; dtAprovacao: string | null;
  observacao: string | null; motivoRejeicao: string | null; itens: ContagemItem[];
}

export interface LogAjuste {
  id: number; localEstoqueId: number; localEstoque: string | null;
  produtoId: number; produto: string | null; subProdutoId: number;
  saldoAnterior: number; saldoContado: number; ajuste: number; motivo: string;
  dtContagem: string; dtAprovacao: string | null;
  contagemOrigemId: number | null; transferenciaOrigemId: number | null; observacao: string | null;
  contagemOrigemNumero: number | null; transferenciaOrigemNumero: number | null;
}

export interface AjusteFiltros {
  localEstoqueId?: number; produtoId?: number; motivo?: string; dtInicio?: string; dtFim?: string;
}

const C = '/api/contagens-ciclo';
const CIC = '/api/ciclos-contagem';

export const cycleService = {
  // ciclos
  listarCiclos: () => apiClient.get<CicloContagem[]>(CIC).then(r => r.data),
  criarCiclo: (req: SalvarCicloRequest) => apiClient.post(CIC, req).then(r => r.data),
  atualizarCiclo: (id: number, req: SalvarCicloRequest) => apiClient.put(`${CIC}/${id}`, req).then(r => r.data),
  excluirCiclo: (id: number) => apiClient.delete(`${CIC}/${id}`).then(r => r.data),
  // contagens
  listar: (pagina = 1, tamanhoPagina = 20, statusId = -1, filtros: { dtInicio?: string; dtFim?: string; localEstoqueId?: number } = {}) =>
    apiClient.get<PaginadoResponse<ContagemResumo>>(C, { params: { pagina, tamanhoPagina, statusId, ...filtros } }).then(r => r.data),
  obter: (id: number) => apiClient.get<ContagemDetalhe>(`${C}/${id}`).then(r => r.data),
  gerar: (cicloId: number | null, localEstoqueId: number, classificacaoAbc: string) =>
    apiClient.post<{ mensagem: string; id: number }>(`${C}/gerar`, { cicloId, localEstoqueId, classificacaoAbc }).then(r => r.data),
  contagem: (id: number, itens: { itemId: number; qtdContada: number }[]) =>
    apiClient.post<{ mensagem: string }>(`${C}/${id}/contagem`, { itens }).then(r => r.data),
  submeter: (id: number) => apiClient.post<{ mensagem: string }>(`${C}/${id}/submeter`).then(r => r.data),
  aprovar: (id: number) => apiClient.post<{ mensagem: string }>(`${C}/${id}/aprovar`).then(r => r.data),
  rejeitar: (id: number, motivo: string) => apiClient.post<{ mensagem: string }>(`${C}/${id}/rejeitar`, { motivo }).then(r => r.data),
  cancelar: (id: number) => apiClient.post<{ mensagem: string }>(`${C}/${id}/cancelar`).then(r => r.data),
  // ajustes
  listarAjustes: (localEstoqueId = 0, motivo?: string) =>
    apiClient.get<LogAjuste[]>('/api/ajustes-estoque', { params: { localEstoqueId, motivo } }).then(r => r.data),
  /** Ajustes paginados; local, motivo, produto, busca (produto), período da contagem e ordenação no servidor. */
  listarAjustesPaginado: (pagina = 1, tamanhoPagina = 20, localEstoqueId = 0, motivo?: string,
                          filtros: FiltrosListaWms & { produtoId?: number } = {}) =>
    apiClient.get<PaginadoResponse<LogAjuste>>('/api/ajustes-estoque/paginado', {
      params: { pagina, tamanhoPagina, localEstoqueId, motivo, ...paramsLista(filtros), produtoId: filtros.produtoId || undefined },
    }).then(r => r.data),
};

// Rótulos de apoio
export const MOTIVOS_DIVERGENCIA = [
  { id: 0, label: 'Sem divergência' },
  { id: 1, label: 'Quebra/avaria no transporte' },
  { id: 2, label: 'Furto/extravio' },
  { id: 3, label: 'Erro de emissão na origem' },
  { id: 4, label: 'Excedente (chegou a mais)' },
  { id: 99, label: 'Outro' },
];

export const TIPOS_GATILHO = [
  { id: 0, label: 'Sempre' },
  { id: 1, label: 'Valor mínimo' },
  { id: 2, label: 'Produto sensível' },
  { id: 3, label: 'Par de locais' },
];
